<template>
  <!--
    Teleport 到 body：避开产品站 .site-content { z-index:1 } 层叠上下文，
    否则 fixed 弹窗再高也会被导航盖住。
  -->
  <Teleport to="body">
    <Transition name="footer-counsel-modal">
      <div v-if="firm" class="footer-counsel-modal" role="presentation">
        <div
          class="footer-counsel-modal__backdrop"
          aria-hidden="true"
          @click="emit('close')"
        />

        <section
          ref="panelRef"
          class="footer-counsel-modal__panel"
          role="dialog"
          aria-modal="true"
          :aria-labelledby="titleId"
          tabindex="-1"
        >
          <header class="footer-counsel-modal__head">
            <div class="footer-counsel-modal__brand">
              <div class="footer-counsel-modal__logo-wrap" aria-hidden="true">
                <img
                  class="footer-counsel-modal__logo"
                  :src="resolveLegalCounselCover(firm.id)"
                  alt=""
                  decoding="async"
                />
              </div>
              <div class="footer-counsel-modal__titles">
                <p class="footer-counsel-modal__location">
                  {{ firm.location }}
                </p>
                <h2 :id="titleId" class="footer-counsel-modal__title font-display">
                  {{ firm.title }}
                </h2>
                <p v-if="englishName" class="footer-counsel-modal__english">
                  {{ englishName }}
                </p>
              </div>
            </div>

            <button
              ref="closeButtonRef"
              type="button"
              class="footer-counsel-modal__close"
              :aria-label="copy.closeModal"
              @click="emit('close')"
            >
              <svg
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden="true"
              >
                <path
                  d="M6 6L18 18M18 6L6 18"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="square"
                />
              </svg>
            </button>
          </header>

          <div class="footer-counsel-modal__scroll">
            <p class="footer-counsel-modal__excerpt">
              {{ firm.excerpt }}
            </p>

            <div class="footer-counsel-modal__facts">
              <div class="footer-counsel-modal__fact">
                <span class="footer-counsel-modal__fact-label">
                  {{ copy.addressLabel }}
                </span>
                <p class="footer-counsel-modal__fact-value">
                  {{ firm.address }}
                </p>
              </div>

              <div v-if="firm.focus.length" class="footer-counsel-modal__fact">
                <span class="footer-counsel-modal__fact-label">
                  {{ copy.focusLabel }}
                </span>
                <ul class="footer-counsel-modal__tags">
                  <li
                    v-for="tag in firm.focus"
                    :key="tag"
                    class="footer-counsel-modal__tag"
                  >
                    {{ tag }}
                  </li>
                </ul>
              </div>
            </div>

            <div class="footer-counsel-modal__body">
              <h3 class="footer-counsel-modal__section-title">
                {{ copy.introTitle }}
              </h3>
              <p v-for="(paragraph, index) in firm.intro" :key="index">
                {{ paragraph }}
              </p>
            </div>
          </div>
        </section>
      </div>
    </Transition>
  </Teleport>
</template>

<script lang="ts">
/**
 * 背景滚动锁定（对齐主站 FooterCounselModal / AppMobileMenu）：
 * 只加 class + 拦截 touch/wheel，绝不改 body position / scrollY。
 * 旧方案 position:fixed + top:-scrollY 在解锁时会先闪顶再 scrollTo，造成「从顶部滚回来」。
 */
let bodyScrollLockCount = 0;
let touchLockBound = false;

const isEventInsideModalScroll = (target: EventTarget | null) => {
  if (!(target instanceof Element)) return false;
  return Boolean(target.closest('.footer-counsel-modal__scroll'));
};

const onDocTouchMove = (event: TouchEvent) => {
  if (isEventInsideModalScroll(event.target)) return;
  event.preventDefault();
};

const onDocWheel = (event: WheelEvent) => {
  if (isEventInsideModalScroll(event.target)) return;
  event.preventDefault();
};

function acquireBodyScrollLock(): boolean {
  if (typeof document === 'undefined') return false;

  if (bodyScrollLockCount === 0) {
    document.body.classList.add('footer-counsel-modal-open');
    document.documentElement.classList.add('footer-counsel-modal-open');
    if (!touchLockBound) {
      document.addEventListener('touchmove', onDocTouchMove, { passive: false });
      document.addEventListener('wheel', onDocWheel, { passive: false });
      touchLockBound = true;
    }
  }
  bodyScrollLockCount += 1;
  return true;
}

function releaseBodyScrollLock() {
  if (typeof document === 'undefined' || bodyScrollLockCount === 0) return;

  bodyScrollLockCount -= 1;
  if (bodyScrollLockCount !== 0) return;

  document.body.classList.remove('footer-counsel-modal-open');
  document.documentElement.classList.remove('footer-counsel-modal-open');
  if (touchLockBound) {
    document.removeEventListener('touchmove', onDocTouchMove);
    document.removeEventListener('wheel', onDocWheel);
    touchLockBound = false;
  }
}
</script>

<script setup lang="ts">
import { computed, nextTick, onUnmounted, ref, useId, watch } from 'vue';
import { resolveLegalCounselCover } from '../consts/gcsaLegalCounselAssets';
import type {
  GcsaLegalCounselFirm,
  GcsaLegalCounselSnapshot
} from '../consts/gcsaLegalCounselContent';

const props = defineProps<{
  firm: GcsaLegalCounselFirm | null;
  copy: GcsaLegalCounselSnapshot;
}>();

const emit = defineEmits<{
  close: [];
}>();

const titleId = `footer-counsel-title-${useId().replace(/[^a-zA-Z0-9_-]/g, '')}`;
const panelRef = ref<HTMLElement | null>(null);
const closeButtonRef = ref<HTMLButtonElement | null>(null);
let previouslyFocusedElement: HTMLElement | null = null;
let ownsBodyScrollLock = false;
let isOpen = false;

const englishName = computed(() => {
  if (!props.firm || props.firm.englishName === props.firm.title) return '';
  return props.firm.englishName;
});

const focusWithoutScroll = (element: HTMLElement | null | undefined) => {
  if (!element) return;
  try {
    element.focus({ preventScroll: true });
  } catch {
    element.focus();
  }
};

const getDeepActiveElement = (): HTMLElement | null => {
  if (typeof document === 'undefined') return null;

  let activeElement: Element | null = document.activeElement;
  while (activeElement instanceof HTMLElement && activeElement.shadowRoot?.activeElement) {
    activeElement = activeElement.shadowRoot.activeElement;
  }
  return activeElement instanceof HTMLElement ? activeElement : null;
};

const getFocusableElements = (): HTMLElement[] => {
  if (!panelRef.value) return [];

  const selector = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])'
  ].join(',');

  return Array.from(panelRef.value.querySelectorAll<HTMLElement>(selector)).filter(
    element => !element.hasAttribute('hidden') && element.getAttribute('aria-hidden') !== 'true'
  );
};

const onKeydown = (event: KeyboardEvent) => {
  if (event.key === 'Escape' && props.firm) {
    event.preventDefault();
    emit('close');
    return;
  }

  if (
    event.key !== 'Tab' ||
    !panelRef.value ||
    !panelRef.value.contains(getDeepActiveElement())
  ) {
    return;
  }

  const focusableElements = getFocusableElements();
  if (focusableElements.length === 0) {
    event.preventDefault();
    focusWithoutScroll(panelRef.value);
    return;
  }

  const firstElement = focusableElements[0];
  const lastElement = focusableElements[focusableElements.length - 1];
  const activeElement = getDeepActiveElement();

  if (event.shiftKey && activeElement === firstElement) {
    event.preventDefault();
    focusWithoutScroll(lastElement);
  } else if (!event.shiftKey && activeElement === lastElement) {
    event.preventDefault();
    focusWithoutScroll(firstElement);
  }
};

const closeDialog = () => {
  if (typeof document === 'undefined' || !isOpen) return;

  document.removeEventListener('keydown', onKeydown);
  if (ownsBodyScrollLock) {
    releaseBodyScrollLock();
    ownsBodyScrollLock = false;
  }

  const focusTarget = previouslyFocusedElement;
  previouslyFocusedElement = null;
  isOpen = false;
  // preventScroll：归还焦点到页脚按钮时不要再触发 scrollIntoView
  if (focusTarget?.isConnected) focusWithoutScroll(focusTarget);
};

const openDialog = async () => {
  if (typeof document === 'undefined' || isOpen) return;

  previouslyFocusedElement = getDeepActiveElement();
  ownsBodyScrollLock = acquireBodyScrollLock();
  document.addEventListener('keydown', onKeydown);
  isOpen = true;

  await nextTick();
  focusWithoutScroll(closeButtonRef.value);
};

watch(
  () => props.firm,
  firm => {
    if (firm) {
      void openDialog();
      return;
    }
    closeDialog();
  },
  { immediate: true }
);

onUnmounted(closeDialog);
</script>
